import { useState, useEffect } from 'react';
import { ref, onValue, query, orderByChild, Unsubscribe } from 'firebase/database';
import { database } from '@config/firebase';
import { useAuth } from '@context/AuthContext';
import { openInquiryChat, sendInquiryMessage } from '../services/secureOperations';
import { ChatMessage } from './useChat';

/**
 * Subscribes to the shared 3-way chat room for a property inquiry
 * (landlord + matched tenant pair). Reuses the same RTDB primitives as the
 * tenant-tenant match chat; the room id is derived server-side from the inquiry.
 */
export const useInquiryChat = (inquiryId: string | undefined) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomId, setRoomId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!inquiryId || !currentUser) {
      setLoading(false);
      return;
    }

    let unsubscribe: Unsubscribe | undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const subscribe = async () => {
      try {
        // Ensures membership server-side and returns the room id.
        const { roomId: resolvedRoomId } = await openInquiryChat(inquiryId);
        if (cancelled) return;
        setRoomId(resolvedRoomId);

        const messagesRef = ref(database, `chatMessages/${resolvedRoomId}`);
        const messagesQuery = query(messagesRef, orderByChild('timestamp'));
        unsubscribe = onValue(
          messagesQuery,
          (snapshot) => {
            const data = snapshot.val() as Record<string, Omit<ChatMessage, 'id'>> | null;
            const messageList = data
              ? Object.entries(data)
                .filter(([, message]) => typeof message?.type === 'string')
                .map(([id, message]) => ({ id, ...message }))
                .sort((a, b) => a.timestamp - b.timestamp)
              : [];
            setMessages(messageList);
            setLoading(false);
          },
          (err) => {
            console.error('Error loading inquiry chat messages:', err);
            setError('Could not load this conversation.');
            setLoading(false);
          }
        );
      } catch (err) {
        console.error('Error opening inquiry chat:', err);
        if (!cancelled) {
          setError('Could not open this conversation.');
          setLoading(false);
        }
      }
    };

    void subscribe();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [inquiryId, currentUser]);

  const sendMessage = async (text: string) => {
    if (!currentUser || !inquiryId || !text.trim()) return;
    await sendInquiryMessage(inquiryId, text.trim());
  };

  return { messages, sendMessage, loading, roomId, error };
};
